/**
 * Copyright Mántum All Rights Reserved.
 */
import { forwardRef, useMemo } from 'react';
import type { Limit, Type } from '@mantum/kanela';

type PropTypes = {
    autocomplete?:
        | 'off'
        | 'email'
        | 'current-password'
        | 'username'
        | 'new-password';
    alert?: 'success' | 'error' | null;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    limit?: Limit;
    max?: number;
    min?: number;
    mode?: 'display' | 'normal';
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    reveal?: boolean;
    resize?: boolean;
    testid?: string;
    mutate?: boolean;
    type: Type;
    value?: string | number;
};

export default forwardRef<HTMLInputElement, PropTypes>(
    function Input(props, ref) {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;
            if (props.onChange) {
                props.onChange(name, value);
            }
        };

        const type = useMemo(() => {
            return props.type === 'date' ? 'text' : props.type;
        }, [props.type]);

        const error = !props.error
            ? 'px-2 border-gray-300'
            : 'pl-2 pr-8 border-2 border-error';

        const alert =
            props.alert === 'success'
                ? 'text-green-500 border-green-600'
                : 'text-red-500 border-red-600';

        const color =
            props.alert !== null && props.alert !== undefined ? alert : '';

        const mode =
            props.mode === 'display'
                ? `text-center text-9xl h-36 border-4 ${color}`
                : 'h-10 text-base';

        const mutate = props.mutate
            ? `border-slate-50 outline-none no-number-spin ${
                  props.resize ? '!text-9xl' : '!text-8xl !text-gray-700'
              } ${mode}`
            : '';

        const classNames = `${error} ${
            props.className ? props.className : ''
        } ${mode && mutate ? mutate : `${mode} outline-primary`}`;

        const optionsLimit = getColorByRange(Number(props.value), props.limit);

        return (
            <input
                autoComplete={props.autocomplete}
                className={`w-full rounded border disabled:bg-transparent disabled:opacity-90 ${classNames}`}
                style={{
                    color: optionsLimit,
                    fontWeight: optionsLimit ? 'bold' : ''
                }}
                data-testid={props.testid}
                disabled={props.disabled}
                max={props.max}
                min={props.min}
                name={props.name}
                onBlur={props.onBlur}
                onChange={handleChange}
                onFocus={props.onFocus}
                onKeyDown={props.onKeyDown}
                placeholder={props.placeholder}
                ref={ref}
                type={type}
                value={props.value}
            />
        );
    }
);

export const getColorByRange = function (value: number, limit?: Limit) {
    if (!limit) {
        return;
    }

    if (value < limit.min.value) {
        return limit.min.color;
    }
    if (value > limit.max.value) {
        return limit.max.color;
    }

    return limit.default;
};
